import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Grid,
  Divider,
} from "@mui/material";
import LanguageIcon from '@mui/icons-material/Language';
import api from "../config/axios";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import dayjs from "dayjs";
import {
  Visibility,
  MoreVert,
  CalendarToday,
  Payment,
  TrackChanges,
  WhatsApp,
  PlayArrow,
  CheckCircle,
  LocalShipping as ShipIcon,
  Cancel,
  Delete,
  Pending,
  LocationOn,
  CheckCircleOutline,
  Warning,
  AttachMoney,
} from "@mui/icons-material";

const getPaymentStatusColor = (status) => {
  const s = status?.toLowerCase();
  if (s === "paid") return { color: "#ffffff", backgroundColor: "#1E7E34" };
  if (s === "paid-advance") return { color: "#ffffff", backgroundColor: "#B76E00" };
  if (s === "pending") return { color: "#ffffff", backgroundColor: "#C62828" };
  return { color: "#ffffff", backgroundColor: "#616161" };
};

const getStatusColor = (status) => {
  const s = status?.toLowerCase();
  if (s === "dispatched" || s === "ready-to-ship") return { color: "#ffffff", backgroundColor: "#B76E00" };
  if (s === "delivered" || s === "accepted") return { color: "#ffffff", backgroundColor: "#1E7E34" };
  if (s === "pending-approval" || s === "in-progress") return { color: "#ffffff", backgroundColor: "#0056D2" };
  if (s === "canceled") return { color: "#ffffff", backgroundColor: "#C62828" };
  return { color: "#ffffff", backgroundColor: "#616161" };
};

const getPaymentStatusIcon = (status) => {
  switch (status) {
    case "paid":
      return <CheckCircle sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "paid-advance":
      return <Payment sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "pending":
      return <Pending sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "refunded":
      return <Cancel sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    default:
      return <Payment sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
  }
};

const getOrderStatusIcon = (status) => {
  switch (status) {
    case "pending-approval":
      return <TrackChanges sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "accepted":
      return <CheckCircleOutline sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "in_progress":
    case "in-progress":
      return <PlayArrow sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "ready":
    case "ready-to-ship":
      return <ShipIcon sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "dispatched":
      return <ShipIcon sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "delivered":
      return <CheckCircle sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "canceled":
      return <Cancel sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    default:
      return <Warning sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
  }
};

const getShiprocketStatusIcon = (status) => {
  switch (status) {
    case "Delivered":
      return <CheckCircle sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "In Transit":
    case "REACHED AT DESTINATION HUB":
      return <ShipIcon sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "Picked Up":
      return <ShipIcon sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    case "cancelled":
      return <Cancel sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
    default:
      return <Warning sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }} />;
  }
};

const getShiprocketStatusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (!s.includes("undelivered") || s.includes("delivered") || s.includes("reached at destination hub")) return { backgroundColor: "#1E7E34", color: "#E6F4EA" };
  if (s.includes("in transit"))
    return { backgroundColor: "#B76E00", color: "#FFF4E5" };
  if (s.includes("picked up"))
    return { backgroundColor: "#0056D2", color: "#EBF3FF" };
  if (s.includes("rto") || s.includes("undelivered")) return { backgroundColor: "#C62828", color: "#FCE8E8" };
  return { backgroundColor: "#616161", color: "#F5F5F5" };
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const OrdersMobileView = ({
  orders,
  loading,
  onViewOrder,
  onLoadMore,
  hasMore,
  loadingMore,
  selectedStatus,
  isDesktop = false,
  onSortToggle,
  sortOrder = "none",
  onRefreshOrders,
  onTrackOrder,
  showShiprocketStatus,
  shiprocketDeliveryStatusByOrderId,
  trackingDataByOrderId,
  shiprocketStatusLoadingByOrderId,
}) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedOrderId, setSelectedOrderId] = React.useState(null);
  const [selectedOrder, setSelectedOrder] = React.useState(null);

  const handleMenuClick = (event, orderId, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrderId(orderId);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrderId(null);
    setSelectedOrder(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedOrder) return;
    try {
      const payload = { id: selectedOrder.id };
      if (newStatus) payload.status = newStatus;
      const response = await api.post('/update_order_status.php', payload);
      if (response.data.success) {
        if (onRefreshOrders) onRefreshOrders();
      }
      handleMenuClose();
    } catch (error) {
      console.error('Error updating order status:', error);
      handleMenuClose();
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    const confirmed = window.confirm(`Are you sure you want to delete order ${selectedOrder.order_id || selectedOrder.id}?\n\nThis action cannot be undone.`);
    if (!confirmed) {
      handleMenuClose();
      return;
    }
    try {
      const response = await api.delete(`/delete_order.php?id=${selectedOrder.id}`);
      if (response.data.success) {
        if (onRefreshOrders) onRefreshOrders();
      }
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting order:', error);
      handleMenuClose();
    }
  };

  const handlePaymentStatusChange = async (newPaymentStatus) => {
    if (!selectedOrder) return;
    try {
      const payload = { id: selectedOrder.id, payment_status: newPaymentStatus };
      const response = await api.post('/update_order_status.php', payload);
      if (response.data.success) {
        if (onRefreshOrders) onRefreshOrders();
      }
      handleMenuClose();
    } catch (error) {
      console.error('Error updating payment status:', error);
      handleMenuClose();
    }
  };

  useEffect(() => {
    if (inView && hasMore && !loadingMore && onLoadMore) {
      onLoadMore();
    }
  }, [inView, hasMore, loadingMore, onLoadMore]);

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      y: -30,
      scale: 0.95,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  const renderOrderCard = (order, index) => (
    <motion.div
      key={order.id}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ delay: index * 0.05 }}
      ref={index === orders.length - 1 ? ref : null}
    >
      <Card
        sx={{
          background: "#FFFFFF",
          borderRadius: "24px",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.03)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          mb: { xs: 3, md: 0 },
          "&:hover": {
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.06)",
            transform: { xs: "none", md: "translateY(-4px)" },
            borderColor: "primary.light",
          },
          height: { md: 'fit-content' }
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: '6px' } }}>
          {/* Header Bar */}
          <Box
            sx={{
              px: { xs: 2.5, sm: 3 },
              py: isDesktop ? 1.5 : 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(135deg, #6355e3ff, #f9cdeeff)",
              borderBottom: "1px solid rgba(0,0,0,0.04)"
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'white', letterSpacing: '0.1em' }}>
                #{order.order_id}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={order.payment_status?.toUpperCase() || "PENDING"}
                  size="small"
                  icon={getPaymentStatusIcon(order.payment_status?.toLowerCase())}
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.6rem",
                    height: "20px",
                    ...getPaymentStatusColor(order.payment_status),
                    borderRadius: '4px',
                    '& .MuiChip-icon': { color: 'inherit', fontSize: '0.7rem' }
                  }}
                />
                {(order.razorpay_order_id || order.razorpay_payment_id) && (
                  <Chip
                    label={isDesktop ? "WEBSITE ORDER" : ""}
                    size="small"
                    icon={<LanguageIcon />}
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.6rem",
                      height: "20px",
                      background: "linear-gradient(135deg, #001373ff 0%, #c8006aff 100%)",
                      color: "#FFFFFF",
                      borderRadius: '4px',
                      border: 'none',
                      '& .MuiChip-icon': {
                        color: 'inherit',
                        fontSize: '0.7rem',
                        marginRight: isDesktop ? undefined : '0px',
                        marginLeft: isDesktop ? undefined : '4px'
                      },
                      '& .MuiChip-label': {
                        display: isDesktop ? 'block' : 'none'
                      },
                      minWidth: isDesktop ? 'auto' : '24px'
                    }}
                  />
                )}
                {(!showShiprocketStatus || !order?.tracking_id || isDesktop) && (
                  <Chip
                    label={order.status?.toUpperCase() || "UNKNOWN"}
                    size="small"
                    icon={getOrderStatusIcon(order.status?.toLowerCase())}
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.6rem",
                      height: "20px",
                      ...getStatusColor(order.status),
                      borderRadius: '4px',
                      '& .MuiChip-icon': { color: 'inherit', fontSize: '0.7rem' }
                    }}
                  />
                )}
                {showShiprocketStatus && order?.tracking_id && (
                  <Chip
                    label={shiprocketStatusLoadingByOrderId?.[order.id] ? "LOADING..." : (shiprocketDeliveryStatusByOrderId?.[order.id]?.toUpperCase() || "PENDING")}
                    size="small"
                    icon={getShiprocketStatusIcon(shiprocketDeliveryStatusByOrderId?.[order.id])}
                    sx={{
                      fontWeight: 900,
                      fontSize: "0.55rem",
                      height: "20px",
                      ...getShiprocketStatusColor(shiprocketDeliveryStatusByOrderId?.[order.id]),
                      borderRadius: '4px',
                      '& .MuiChip-icon': { color: 'inherit' }
                    }}
                  />
                )}
                {order.courier_name?.toLowerCase().includes('shipway') ? (
                  <Box sx={{
                    bgcolor: 'white',
                    height: '20px',
                    px: 1,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <img
                      src="https://ishqnamaart.in/shipway.png"
                      alt="Shipway"
                      style={{ width: 'auto', height: '14px' }}
                    />
                  </Box>
                ) : order.courier_name?.toLowerCase().includes('delhivery') ? (
                  <Box sx={{
                    bgcolor: 'white',
                    height: '20px',
                    px: 1,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/2/23/Delhivery_Logo_%282019%29.png"
                      alt="Delhivery"
                      style={{ height: '10px' }}
                    />
                  </Box>
                ) : (
                  <Box sx={{
                    bgcolor: '#ffffffff',
                    height: '20px',
                    px: 1,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/0/00/SR-logo-wiki.png"
                      alt="Shiprocket"
                      style={{ width: 'auto', height: '20px' }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {isDesktop && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={() => onViewOrder(order.id)}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
                    color: "#FFFFFF !important",
                    fontWeight: 900,
                    textTransform: "none",
                    borderRadius: '8px',
                    px: 2,
                    py: 0.5,
                    fontSize: "0.75rem",
                    boxShadow: '0 2px 8px rgba(181, 29, 110, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                    }
                  }}
                >
                  Details
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!order?.tracking_id}
                  onClick={() => {
                    if (onTrackOrder && order?.tracking_id) {
                      onTrackOrder(order);
                    } else if (order?.tracking_id) {
                      const courierName = (order.courier_name || "").toLowerCase();
                      if (courierName.includes("shipway")) {
                        window.open(`https://ishqnama.shipway.com/tracking/forward/${order.tracking_id}/`, '_blank');
                      } else if (courierName.includes("delhivery")) {
                        window.open(`https://www.delhivery.com/track/package/${order.tracking_id}`, '_blank');
                      } else {
                        window.open(`https://shiprocket.co/tracking/${order.tracking_id}`, '_blank');
                      }
                    }
                  }}
                  sx={{
                    borderColor: order?.tracking_id ? "primary.main" : "rgba(0,0,0,0.1)",
                    color: order?.tracking_id ? "primary.main" : "text.disabled",
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.75rem",
                  }}
                >
                  Track
                </Button>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, order.id, order)}
                  sx={{
                    color: "text.secondary",
                    background: 'rgba(0,0,0,0.03)',
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <MoreVert sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Body Content */}
          <Box sx={{ px: { xs: 2.5, sm: 3 }, py: isDesktop ? 2 : 3 }}>
            <Box sx={{ mb: isDesktop ? 1.5 : 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                {order.display_bride_name || "N/A"} & {order.display_groom_name || "N/A"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: isDesktop ? 'flex' : 'block',
                width: '100%',
                justifyContent: isDesktop ? 'space-between' : 'initial',
                alignItems: isDesktop ? 'center' : 'initial',
              }}
            >
              <Grid container spacing={isDesktop ? 0 : 4} alignItems="center" sx={{ width: '100%' }}>
                <Grid item xs={12} md={isDesktop ? true : 3} sx={{ flex: isDesktop ? 1 : 'none' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                    Marriage Day
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: isDesktop ? '0.75rem' : 'inherit' }}>
                    <CalendarToday sx={{ fontSize: 12, color: 'primary.main' }} /> {formatDate(order.dom)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={isDesktop ? true : 3} sx={{ flex: isDesktop ? 1 : 'none' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                    Order Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: isDesktop ? '0.75rem' : 'inherit' }}>
                    <CalendarToday sx={{ fontSize: 12, color: 'primary.main' }} /> {formatDate(order.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={isDesktop ? true : 1} sx={{ flex: isDesktop ? 1 : 'none' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                    Total
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: isDesktop ? '0.8rem' : 'inherit' }}>
                    ₹{order.order_amount?.toLocaleString() || "0"}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={isDesktop ? true : 1} sx={{ flex: isDesktop ? 1 : 'none' }}>
                  <Typography variant="caption" sx={{ color: '#B51D6E', fontWeight: 900, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                    Advance
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900, color: '#B51D6E', fontSize: isDesktop ? '0.8rem' : 'inherit' }}>
                    ₹{(order.advance_amount || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={isDesktop ? true : 2} sx={{ flex: isDesktop ? 1 : 'none' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                    Contact
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: isDesktop ? '0.75rem' : 'inherit' }}>
                    {order.phone || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={isDesktop ? true : 2} sx={{ flex: isDesktop ? 1 : 'none' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                    Address
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: order?.has_address ? '#059669' : '#DC2626',
                    }}
                  >
                    <LocationOn sx={{ fontSize: 12 }} />
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: isDesktop ? '0.65rem' : 'inherit' }}>
                      {order?.has_address ? "Submitted" : "Missing"}
                    </Typography>
                  </Box>
                </Grid>

                {isDesktop && showShiprocketStatus && trackingDataByOrderId?.[order.id] && (
                  <>
                    <Grid item md={true} sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                        Destination
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.75rem' }}>
                          {trackingDataByOrderId[order.id].destination || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item md={true} sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block', mb: 0.2 }}>
                        Expected
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.75rem' }}>
                        {trackingDataByOrderId[order.id].expected_delivery ? dayjs(trackingDataByOrderId[order.id].expected_delivery).format('DD MMM') : "N/A"}
                      </Typography>
                    </Grid>
                  </>
                )}

                {!isDesktop && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1, opacity: 0.5 }} />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>

          {/* Action Row - Mobile Only */}
          {!isDesktop && (
            <Box
              sx={{
                px: { xs: 2.5, sm: 3 },
                py: 1.5,
                borderTop: "1px solid rgba(0,0,0,0.04)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={() => onViewOrder(order.id)}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
                    color: "#FFFFFF !important",
                    fontWeight: 900,
                    textTransform: "none",
                    borderRadius: '12px',
                    px: 3,
                    py: 1,
                    fontSize: "0.8rem",
                    boxShadow: '0 4px 12px rgba(181, 29, 110, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                      boxShadow: '0 6px 15px rgba(181, 29, 110, 0.35)',
                    }
                  }}
                >
                  View Details
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<TrackChanges sx={{ fontSize: 16 }} />}
                  size="small"
                  disabled={!order?.tracking_id}
                  onClick={() => {
                    if (onTrackOrder && order?.tracking_id) {
                      onTrackOrder(order);
                    } else if (order?.tracking_id) {
                      const courierName = (order.courier_name || "").toLowerCase();
                      if (courierName.includes("shipway")) {
                        window.open(`https://ishqnama.shipway.com/tracking/forward/${order.tracking_id}/`, '_blank');
                      } else if (courierName.includes("delhivery")) {
                        window.open(`https://www.delhivery.com/track/package/${order.tracking_id}`, '_blank');
                      } else {
                        window.open(`https://shiprocket.co/tracking/${order.tracking_id}`, '_blank');
                      }
                    }
                  }}
                  sx={{
                    borderColor: order?.tracking_id ? "primary.main" : "rgba(0,0,0,0.1)",
                    color: order?.tracking_id ? "primary.main" : "text.disabled",
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: '12px',
                    px: 2.5,
                    py: 1,
                    fontSize: "0.8rem",
                  }}
                >
                  Track
                </Button>
              </Box>

              <IconButton
                size="small"
                onClick={(e) => handleMenuClick(e, order.id, order)}
                sx={{
                  color: "text.secondary",
                  background: 'rgba(0,0,0,0.03)',
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.08)",
                  },
                }}
              >
                <MoreVert />
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ width: "100%", px: { xs: 1, sm: 2 } }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{ height: 160, mb: 3, borderRadius: '24px' }}
          />
        ))}
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 700, mb: 1 }}>No orders found</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>Try adjusting your filters or search terms</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {isDesktop ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <AnimatePresence>
            {orders.map((order, index) => (
              <Box key={order.id} sx={{ width: '100%' }}>
                {renderOrderCard(order, index)}
              </Box>
            ))}
          </AnimatePresence>
        </Box>
      ) : (
        <Box sx={{ px: 0 }}>
          <AnimatePresence>
            {orders.map((order, index) => (
              <div key={order.id} ref={index === orders.length - 1 ? ref : null}>
                {renderOrderCard(order, index)}
              </div>
            ))}
          </AnimatePresence>
        </Box>
      )}

      {loadingMore && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={40} sx={{ color: "primary.main" }} />
        </Box>
      )}

      {!hasMore && orders.length > 0 && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", fontWeight: 600 }}>
            End of Master Collection
          </Typography>
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { borderRadius: '16px', mt: 1, minWidth: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {selectedOrder && (
          <>
            <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>Update Status</Typography>
            {selectedOrder.status === 'pending-approval' && (
              <MenuItem onClick={() => handleStatusChange('accepted')}>
                <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>Approve Order</ListItemText>
              </MenuItem>
            )}
            {selectedOrder.status === 'accepted' && (
              <MenuItem onClick={() => handleStatusChange('in-progress')}>
                <ListItemIcon><PlayArrow fontSize="small" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>In Progress</ListItemText>
              </MenuItem>
            )}
            {selectedOrder.status === 'dispatched' && (
              <MenuItem onClick={() => handleStatusChange('delivered')}>
                <ListItemIcon><PlayArrow fontSize="small" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>Delivered</ListItemText>
              </MenuItem>
            )}
            {selectedOrder.status === 'in-progress' && (
              <MenuItem onClick={() => handleStatusChange('ready-to-ship')}>
                <ListItemIcon><ShipIcon fontSize="small" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>Ready to Ship</ListItemText>
              </MenuItem>
            )}
            {selectedOrder.status === 'ready-to-ship' && (
              <MenuItem onClick={() => handleStatusChange('dispatched')}>
                <ListItemIcon><ShipIcon fontSize="small" /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>Dispatch</ListItemText>
              </MenuItem>
            )}

            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>Financials</Typography>
            <MenuItem onClick={() => handlePaymentStatusChange(selectedOrder.payment_status === 'paid' ? 'pending' : 'paid')}>
              <ListItemIcon><Payment fontSize="small" sx={{ color: selectedOrder.payment_status === 'paid' ? 'warning.main' : 'success.main' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>{selectedOrder.payment_status === 'paid' ? 'Mark as Unpaid' : 'Mark as Paid'}</ListItemText>
            </MenuItem>

            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleDeleteOrder} sx={{ color: 'error.main' }}>
              <ListItemIcon><Delete fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>Delete Order</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default OrdersMobileView;
